import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {InvalidateSubject} from '@shared/helpers/invalidate.subject';
import {CommandService} from '@core/services/command.service';
import {CreateOccurrencesCommand, UpdateOccurrenceSettingsCommand} from '../../commands/task-manager.command';
import {GraphqlContentScope} from '@shared/models/graphql-content-scope.model';
import {RecurrenceInfo, TaskRecurrence} from '../../models/task-manager.model';
import {getCurrentProject, ProjectState} from '../../../projects/store/reducers/projects.reducer';
import {select, Store} from '@ngrx/store';
import {take, takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {IQueryState} from '@shared/store/reducers/query.reducer';
import {getQueryResponse} from '@shared/store/selectors/query.selector';
import {QueryProvider} from '@shared/queries/query-provider';
import {Configuration} from '@shared/models/configuration';
import {TimeZoneService} from '@core/services/time-zone.service';
import {TaskManagerService} from '../../services/task-manager.service';
import {PlatformCommand} from '@shared/commands/command-base';
import {DialogService} from '@shared/services/dialog-service';
import {IVirtualGridCollectionState} from '@shared/store/reducers/virtual-grid.reducer';
import {getVirtualGridItemsMetaData} from '@shared/store/selectors/virtual-grid.selector';
import {SnackBarService} from '@core/services/snack-bar.service';
import {DateUtility} from '@shared/utility/date-utility';
import {FuseSplashScreenService} from '@barebone-fuse-services/splash-screen.service';
import moment from 'moment';

@Component({
    standalone: false,
    selector: 'particle-task-occurrence',
  templateUrl: './task-occurrence.component.html',
  styleUrls: ['./task-occurrence.component.scss']
})
export class TaskOccurrenceComponent implements OnInit, OnDestroy {
  recurrenceInfos: RecurrenceInfo[];
  selectedRecurrenceInfo: RecurrenceInfo;
  taskFormGroup: FormGroup;
  sourceKey: string;
  invalidateSubject: InvalidateSubject;
  canCreateOccurrences: boolean;
  dynamicData: any;
  data: any;
  scope: number;
  card: any;
  recurrence: TaskRecurrence;
  weekDays: any[];
  unsubscribe$ = new Subject<void>();
  isEndingTypeApplicable: boolean;
  isClose = false;
  hasOccurrencePermission: boolean;
  startDateEnabled: boolean;
  dueDateEnabled: boolean;
  minStartDate: Date;
  maxStartDate: Date;
  minDueDate: Date;
  parameters: any;
  maxDueDate: Date;
  endsTypes: any[];
  enableCreateOccurrence: boolean;
  startDateDueDateErrorMessage: string;
  isSubTask = false;
  private ngUnsubscribe$ = new Subject<void>();
  private id = 'task-occurrence-view';
  serviceName = 'OccurrencesDynamicItemService';

  constructor(private formBuilder: FormBuilder,
              private commandService: CommandService,
              private projectStore: Store<ProjectState>,
              private queryStore: Store<IQueryState>,
              private dialogService: DialogService,
              private taskManagerService: TaskManagerService,
              private timeZoneService: TimeZoneService,
              private virtualGridStore: Store<IVirtualGridCollectionState>,
              private snackBarService: SnackBarService,
              private fuseSplashScreenService: FuseSplashScreenService
  ) {
    this.setDefaultDateRange();
    this.startDateDueDateErrorMessage = 'Please make sure the due date is not set before the start date.';
    this.isEndingTypeApplicable = false;
    this.taskFormGroup = this.formBuilder.group({
      recurrenceInfo: ['', Validators.required],
      endingType: ['Never', Validators.required],
      endingDate: ['', Validators.required],
      endingOccurrenceCount: [1, Validators.required],
      taskStartDate: ['', Validators.required],
      taskDueDate: []
    });
    this.sourceKey = 'SubTasks';
    this.invalidateSubject = new InvalidateSubject();
    this.canCreateOccurrences = false;
    this.weekDays = [
      {
        'title': 'S',
        'isChecked': false,
        'value': 0
      },
      {
        'title': 'M',
        'isChecked': false,
        'value': 1
      },
      {
        'title': 'T',
        'isChecked': false,
        'value': 2
      },
      {
        'title': 'W',
        'isChecked': false,
        'value': 3
      },
      {
        'title': 'T',
        'isChecked': false,
        'value': 4
      },
      {
        'title': 'F',
        'isChecked': false,
        'value': 5
      },
      {
        'title': 'S',
        'isChecked': false,
        'value': 6
      }
    ];

    this.endsTypes = [
      {
        title: 'Never',
        key: 'Never'
      },
      {
        title: 'On Date',
        key: 'On'
      },
      {
        title: 'After Number of Occurrences',
        key: 'After'
      }
    ];
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnInit() {
    this.taskManagerService.taskApi = this.dynamicData.parameters?.taskApi ?? 'TaskManagerApi';
    this.parameters = {
      TicketId: this.dynamicData.card.id,
      Scope: this.dynamicData.card.scopeInt,
      HasVirtualGridConfig: true,
      Api: this.taskManagerService.api,
      SourceType: this.sourceKey
    };
    this.queryStore.pipe(select(getQueryResponse, 'RecurrenceInfos')).subscribe(state => {
      if (state && state.recurrenceInfos && state.recurrenceInfos.length > 0) {
        this.recurrenceInfos = state.recurrenceInfos;
        this.scope = this.dynamicData.board.scopeInt ? this.dynamicData.board.scopeInt : GraphqlContentScope.getNumberScope(this.dynamicData.board.scope);
        this.card = this.dynamicData.card;
        this.isSubTask = this.card?.parentTicket !== null;

        const config = Configuration.getSmartGridConfig(this.sourceKey);
        config.Parameters['TICKET_ID'] = this.card.id;
        config.Parameters['SCOPE'] = GraphqlContentScope.getNumberScope(this.card.scope);
        config.Parameters['OCCURRENCE_ONLY'] = true;
        this.invalidateSubject.invalidate();

        this.recurrence = this.card.recurrence;
        this.recurrence.endingDate = new Date(this.recurrence && this.recurrence.endingDate ? this.timeZoneService.getDisplayDate(this.recurrence.endingDate) : '');
        this.recurrenceInfos.forEach(o => {
          o.dayOfWeeks = this.recurrence.recurrenceInfo.dayOfWeeks;
          o.repeatIntervel = this.recurrence.recurrenceInfo.repeatIntervel;
          o.quarterEnd = this.recurrence.recurrenceInfo.quarterEnd;
        });
        this.selectedRecurrenceInfo = this.recurrenceInfos.find(o => o.type === this.recurrence.recurrenceInfo.type);
        this.isEndingTypeApplicable = this.selectedRecurrenceInfo.type !== 'OneTime';
        if (this.selectedRecurrenceInfo.dayOfWeeks.length === 0) {
          const today = new Date();
          const currentWeekDay = today.getDay();
          this.recurrenceInfos.forEach(o => {
            o.dayOfWeeks = [currentWeekDay];
          });
        }
        // start date and due date configure

        this.startDateEnabled = !!(this.card.startDate || this.dynamicData.startDate) || !!(this.card.taskStartDate || this.dynamicData.taskStartDate);
        this.dueDateEnabled = !!(this.card.dueDate || this.dynamicData.dueDate);

        let startDate = this.card && this.card.taskStartDate ? this.timeZoneService.getDisplayDate(this.card.taskStartDate) : (((!this.card) || (this.card && !this.card.taskStartDate)) ? new Date(this.timeZoneService.getDisplayDate(this.dynamicData.taskStartDate)) : '');

        if (!this.isValidDate(startDate)) {
          startDate = this.card && this.card.startDate ? this.timeZoneService.getDisplayDate(this.card.startDate) : (((!this.card) || (this.card && !this.card.startDate)) ? new Date(this.timeZoneService.getDisplayDate(this.dynamicData.startDate)) : '');
        }

        const dueDate = this.card && this.card.dueDate ? this.timeZoneService.getDisplayDate(this.card.dueDate) : (((!this.card) || (this.card && !this.card.dueDate)) ? new Date(this.timeZoneService.getDisplayDate(this.dynamicData.dueDate)) : '');
        this.setMinDate(this.card, startDate, dueDate);
        this.taskFormGroup.controls['taskStartDate'].setValue(startDate);
        this.taskFormGroup.controls['taskDueDate'].setValue(dueDate);
        this.showCreateOccurrenceSection();
        // array.includes doesn't work with ES5. so, used indexOf instead.
        this.weekDays.forEach(o => {
          o['isChecked'] = this.selectedRecurrenceInfo.dayOfWeeks.indexOf(o['value']) >= 0;
        });
        this.taskFormGroup.controls['recurrenceInfo'].setValue(this.selectedRecurrenceInfo);
        this.taskFormGroup.controls['endingOccurrenceCount'].setValue(this.recurrence.endingOccuranceCount);
        this.taskFormGroup.controls['endingType'].setValue(this.recurrence.endingType);
        this.canCreateOccurrences = this.selectedRecurrenceInfo.type !== 'OneTime';
        console.log(this.recurrence);
        if (this.recurrence.endingDate.getFullYear() < 1990) {
          this.projectStore.select(getCurrentProject)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((project) => {
              if (project && project.TaxYearEnd) {
                this.taskFormGroup.controls['endingDate'].setValue(this.timeZoneService.getDisplayDate(project.TaxYearEnd));
                console.log('this.projectStore.select(getCurrentProject)');
                this.saveRecurrenceSettings();
              }
            });
        } else {
          this.taskFormGroup.controls['endingDate'].setValue(this.timeZoneService.getDisplayDateFormISO(this.timeZoneService.dateTostringWithoutUTC(this.recurrence.endingDate)));
        }
      }
    });
    const queryModel = this.taskManagerService.getQueryModel('RecurrenceInfos');
    queryModel.api = this.taskManagerService.api;
    QueryProvider.postQuery(queryModel, 'RecurrenceInfos', this.queryStore);
    this.virtualGridStore.pipe(select(getVirtualGridItemsMetaData, this.sourceKey), takeUntil(this.ngUnsubscribe$))
      .subscribe((res: any) => {
          if (res && res.HasOccurrencePermission) {
            console.log('[HasOccurrencePermission]', res.HasOccurrencePermission);
            this.hasOccurrencePermission = res.HasOccurrencePermission === 'True';
          } else if (this.dynamicData && this.dynamicData.card) {
            this.hasOccurrencePermission = this.dynamicData.card.hasPermission;
          } else {
            this.hasOccurrencePermission = true;
          }
        }
      );
  }

  onClickCreateOccurrences() {
    console.log('onClickCreateOccurrences');
    this.fuseSplashScreenService.show();
    const dueDate = this.taskFormGroup.controls['taskDueDate'].value;
    const startDate = this.taskFormGroup.controls['taskStartDate'].value;

    if (this.startDateDueDateError(startDate, dueDate)) {
      this.fuseSplashScreenService.hide();
      this.snackBarService.showError(this.startDateDueDateErrorMessage);
      return;
    }
    const command = new CreateOccurrencesCommand(this.card.id, this.taskFormGroup.controls['endingOccurrenceCount'].value, 10);
    const platformCommand = new PlatformCommand('CreateOccurrencesPlatformCommand', this.taskManagerService.api);
    platformCommand.fieldValues = {
      CreateOccurrencesCommand: command
    };
    this.commandService.executePlatformCommand(platformCommand).pipe().subscribe(state => {
      console.log(state);
      this.fuseSplashScreenService.hide();
      this.invalidateSubject.invalidate();
      this.invalidateTaskViews(this.data.input.card, true);
    }, err => {
      if (err.Payload) {
        this.fuseSplashScreenService.hide();
        this.snackBarService.showError(err.Payload);
      }
    });
  }

  invalidateTaskViews(card: any = null, invalidateTaskToolbarNavigationSystem = true) {
    if (this.data.input.taskInvalidator) {
      if (!card) {
        card = this.data.input.card;
      }
      this.data.input.taskInvalidator.next({card: card, sourceViewId: this.id, invalidateTaskToolbarNavigationSystem: invalidateTaskToolbarNavigationSystem});
    }
  }

  startDateDueDateError(startDate, dueDate) {
    if (DateUtility.isValidDate(startDate) && DateUtility.isValidDate(dueDate) &&
      startDate.getTime() > dueDate.getTime()) {
      return true;
    } else {
      return false;
    }
  }

  onRepeatChanged() {
    console.log('onRepeatChanged');
    this.saveRecurrenceSettings();
  }

  onEndingDate($event) {
    console.log('onEndingDate');
    this.saveRecurrenceSettings();
  }

  endingOccurrenceCountChanged() {
    console.log('endingOccurrenceCountChanged');
    this.saveRecurrenceSettings();
  }

  onEndingTypeChanged($event) {
    if (this.recurrence.endingType !== this.taskFormGroup.controls['endingType'].value) {
      console.log('onEndingTypeChanged');
      this.saveRecurrenceSettings();
    }
  }

  onGridCellValueChange($event) {
    $event.item.minDate = new Date();

    if ($event.action) {
      switch ($event.action.id) {
        case 'Open':
          this.navigate($event.item.cells.Title.value);
          break;
        case 'Delete':
          this.taskManagerService.deleteTask($event.item.cells.Title.value, this.invalidateSubject, true, null, this.invalidateTaskViews.bind(this));
          break;
        case 'Edit':
          this.openEditor($event);
          break;
        case 'Disable':
          this.taskManagerService.toggleTicketState($event.item.cells.Title.value, true,
            this.invalidateSubject, false);
          break;
        case 'Enable':
          this.taskManagerService.toggleTicketState($event.item.cells.Title.value, false,
            this.invalidateSubject, false);
          break;

      }
    } else {
      this.navigate($event.item.cells.Title.value);
    }
  }

  navigate(card: any) {
    console.log('open occurrance:', card);
    this.invalidateTaskViews(card, true);
/*    const result = {
      action: 'Redirect',
      board: this.data.input.board,
      card: card
    };
    const dialogRef = this.data['dialogRef'];
    dialogRef.close(result);*/
  }

  onSelectionChanged($event) {

  }

  clickWeekDays(weekDay) {
    weekDay.isChecked = !weekDay.isChecked;
    this.saveRecurrenceSettings();
  }

  closeNote() {
    this.isClose = true;
  }

  onTaskStartDateChange() {
    const dueDate = this.taskFormGroup.controls['taskDueDate'].value;
    const startDate = this.taskFormGroup.controls['taskStartDate'].value;

    // this.minDueDate = new Date(startDate);
    // if (this.startDateEnabled && this.isValidDate(startDate)) {
    //   this.data.input.card.taskStartDate = startDate;
    //   if (this.dueDateEnabled && this.isValidDate(dueDate)) {
    //     if (dueDate < startDate) {
    //       // this.data.input.card.dueDate = startDate;
    //       // this.taskFormGroup.controls['taskDueDate'].setValue(startDate);
    //     } else {
    //       this.data.input.card.dueDate = dueDate;
    //     }
    //   } else {
    //     // this.data.input.card.dueDate = startDate;
    //     // this.taskFormGroup.controls['taskDueDate'].setValue(startDate);
    //   }
    //   this.dueDateEnabled = true;
    // } else {
    //   this.data.input.card.taskStartDate = null;
    // }

    if (this.dueDateEnabled && DateUtility.isValidDate(dueDate)) {
      this.data.input.card.dueDate = dueDate;
    } else {
      this.data.input.card.dueDate = null;
    }


    if (this.startDateEnabled && this.isValidDate(startDate)) {
      this.data.input.card.taskStartDate = startDate;
    } else {
      this.data.input.card.taskStartDate = null;
    }


    if (this.startDateDueDateError(startDate, dueDate)) {
      this.snackBarService.showError(this.startDateDueDateErrorMessage);
      return;
    }


    //this.taskManagerService.ExecuteAddOrUpdateTask(this.data.input);
    this.updateTask(this.data.input.card, this.data.input.board);
    this.showCreateOccurrenceSection();
  }

  updateTask(card: any, board: any) {
    const updateTaskCommand = this.taskManagerService.getAddOrUpdateTaskCommand(board, card);
    // updateTaskCommand.isCamelCase = true;
    this.commandService.executePlatformCommand(updateTaskCommand).pipe(take(1)).subscribe(response => {
      if (response) {
        this.invalidateTaskViews(this.data.input.card, false);
      }
    }, err => {
      this.snackBarService.showError('Date update failed failed!');
    });
  }

  onTaskDueDateChange() {
    const dueDate = this.taskFormGroup.controls['taskDueDate'].value;
    const startDate = this.taskFormGroup.controls['taskStartDate'].value;

    // if (this.dueDateEnabled && this.isValidDate(dueDate)) {
    //   this.data.input.card.dueDate = dueDate;
    //   if (this.startDateEnabled && this.isValidDate(startDate)) {
    //     this.data.input.card.taskStartDate = startDate;
    //   } else {
    //     this.data.input.card.taskStartDate = dueDate;
    //     this.taskFormGroup.controls['taskStartDate'].setValue(dueDate);
    //   }
    //   this.startDateEnabled = true;
    // } else {
    //   this.data.input.card.dueDate = null;
    // }

    if (this.dueDateEnabled && DateUtility.isValidDate(dueDate)) {
      this.data.input.card.dueDate = dueDate;
    } else {
      this.data.input.card.dueDate = null;
    }

    if (this.startDateEnabled && this.isValidDate(startDate)) {
      this.data.input.card.taskStartDate = startDate;
    } else {
      this.data.input.card.taskStartDate = null;
    }


    if (this.startDateDueDateError(startDate, dueDate)) {
      this.snackBarService.showError(this.startDateDueDateErrorMessage);
      return;
    }


    //this.taskManagerService.ExecuteAddOrUpdateTask(this.data.input);
    this.updateTask(this.data.input.card, this.data.input.board);
    this.showCreateOccurrenceSection();
  }

  isValidDate(date) {
    return date instanceof Date && !isNaN(date.getTime());
  }

  /*saveFieldDefinition(fieldDefinition: any, selectedFieldSection: any) {
    let fieldSettings = null;
    if (fieldDefinition && fieldDefinition.configKey) {
      const fieldConfig = Configuration.getSmartGridConfig(fieldDefinition.configKey);
      if (fieldConfig && fieldConfig.FieldSettings) {
        fieldSettings = fieldConfig.FieldSettings;
      }
    }
    if (fieldSettings) {
      const rootUrlKey = Configuration.getSmartGridConfig(this.sourceKey).RootUrlKey;
      const url = `${Configuration.getConfig(rootUrlKey)}FieldSection/UpdateFieldDefinition`;
      const config = Configuration.getSmartGridConfig(this.sourceKey);
      if (config) {
        const sourceType = config.SourceType;
        const updateFieldDefinitionCommand = new UpdateFieldDefinitionCommand([fieldDefinition], selectedFieldSection.id,
          ParticleConfig.ApplicationId, sourceType, true, url, !Configuration.getSmartGridConfig(this.sourceKey).IsCompanySpecific);
        this.commandService.executeCommand(updateFieldDefinitionCommand).pipe().subscribe(commandState => {
          this.invalidateSubject.invalidate();
        });
      }
    }
  }*/

  private saveRecurrenceSettings() {
    this.updateModels();
    const command = new UpdateOccurrenceSettingsCommand(this.card.id, this.recurrence);
    const updaterOccurrenceSettingsPlatformCommand = new PlatformCommand('UpdateOccurrenceSettingsPlatformCommand', this.taskManagerService.api);
    updaterOccurrenceSettingsPlatformCommand.fieldValues = {
      UpdateOccurrenceSettingsCommand: command
    };
    this.commandService.executePlatformCommand(updaterOccurrenceSettingsPlatformCommand).pipe().subscribe(state => {
      console.log(state);
    });
  }

  private updateModels() {
    this.canCreateOccurrences = this.taskFormGroup.value.recurrenceInfo.type !== 'OneTime';
    this.selectedRecurrenceInfo = this.taskFormGroup.value.recurrenceInfo;
    this.recurrence.recurrenceInfo = this.selectedRecurrenceInfo;
    this.isEndingTypeApplicable = this.selectedRecurrenceInfo.type !== 'OneTime';
    this.recurrence.endingOccuranceCount = this.taskFormGroup.controls['endingOccurrenceCount'].value;
    this.recurrence.endingDate = this.timeZoneService.getUTCDateWithoutTime(this.taskFormGroup.controls['endingDate'].value);
    this.recurrence.endingType = this.taskFormGroup.controls['endingType'].value;
    this.recurrence.recurrenceInfo.dayOfWeeks = [];
    this.weekDays.forEach(o => {
      if (o.isChecked) {
        this.recurrence.recurrenceInfo.dayOfWeeks.push(o.value);
      }
    });
  }

  private openEditor(event) {
    if (!this.hasOccurrencePermission) {
      this.snackBarService.showError('You do not have enough permission to perform this operation.');
        return;
    }
    const minWidth = '800px';
    const maxWidth = '800px';

    this.dialogService.showCellEditorDialog(event.item, event.fieldDefinition, minWidth, maxWidth, this.sourceKey).subscribe(state => {
      console.log('After Edit', state);
      if (state.action === 'OK') {
        // this.saveFieldDefinition(state.data.input.fieldDefinition, event.fieldSection);
        const startDate = typeof event.item.cells.StartDate.value === 'string' ?
          new Date(event.item.cells.StartDate.value) : event.item.cells.StartDate.value;

        const endDate = typeof event.item.cells.DueDate.value === 'string' ?
          new Date(event.item.cells.DueDate.value) : event.item.cells.DueDate.value;

        if (this.startDateDueDateError(startDate, endDate)) {
          this.snackBarService.showError(this.startDateDueDateErrorMessage);
          return;
        }

        const item = {id: event.item.id, fieldValues: {}};
        const value = event.item.cells[event.fieldDefinition.key].value;


        const command = new PlatformCommand('EditTaskCommand', 'TaskManagerApi');
        command.fieldValues[event.fieldDefinition.key] = value;
        command.fieldValues['Id'] = event.item.id;
        this.commandService.executePlatformCommand(command).pipe().subscribe(commandState => {
          this.invalidateSubject.invalidate();
        });
      }
    });
  }

  private showCreateOccurrenceSection() {
    const startDate = this.taskFormGroup.controls['taskStartDate'].value;
    const dueDate = this.taskFormGroup.controls['taskDueDate'].value;
    if (this.startDateEnabled && this.isValidDate(startDate) && this.dueDateEnabled && this.isValidDate(dueDate)) {
      this.enableCreateOccurrence = true;
    } else {
      this.enableCreateOccurrence = false;
    }
  }

  private setDefaultDateRange() {
    this.minStartDate = new Date(moment().format());
    this.minDueDate = new Date(moment().add(1, 'days').format());
    this.maxStartDate = new Date(moment(this.minStartDate).add(10, 'y').format());
    this.maxDueDate = new Date(moment(this.minDueDate).add(10, 'y').format());
  }

  private setMinDate(card: any, startDate: any, dueDate: any) {
    const isSubTask = card?.parentTicket !== null;
    if (!isNaN(startDate.getTime())) {
      this.minStartDate = new Date(startDate);
      this.minDueDate = this.maxStartDate;
    }
    if (isSubTask) {
      const parentStartDate = new Date(card?.parentTicket?.startDate);
      const parentDueDate = new Date(card?.parentTicket?.dueDate);
      if (!isNaN(parentStartDate.getTime())) {
        this.minDueDate = parentStartDate;
        this.minStartDate = parentStartDate;
      }

      if (!isNaN(parentDueDate.getTime())) {
        this.maxDueDate = parentDueDate;
        this.maxStartDate = parentDueDate;
      }
    } else {
      if (!isNaN(dueDate.getTime())) {
        this.minDueDate = new Date(dueDate);
      }
    }
  }
}
